/**
 * Conversation pending storage — used when user goes from Entry Conversation to Canvas.
 * Pending data is restored when user clicks "Back to Conversation" in the sidebar.
 */

import type { ChatMessage } from "../data/diagramStorage";

const KEY_MESSAGES_PENDING = "ideaweave:convMessagesPending";
const KEY_TOPIC_PENDING = "ideaweave:convTopicPending";

/** Export for use in useAppView when syncing with active conversation keys */
export { KEY_MESSAGES_PENDING, KEY_TOPIC_PENDING };

/**
 * Get pending conversation raw strings and clear Pending keys. Used when restoring from canvas back to conversation.
 */
export function getAndClearPendingRaw(): {
  messages: string | null;
  topic: string | null;
} {
  if (typeof window === "undefined") {
    return { messages: null, topic: null };
  }
  try {
    const messages = window.sessionStorage.getItem(KEY_MESSAGES_PENDING);
    const topic = window.sessionStorage.getItem(KEY_TOPIC_PENDING);
    window.sessionStorage.removeItem(KEY_MESSAGES_PENDING);
    window.sessionStorage.removeItem(KEY_TOPIC_PENDING);
    return { messages, topic };
  } catch {
    return { messages: null, topic: null };
  }
}

/**
 * Write raw conversation to Pending keys. Used when user clicks "Go to Draw" to backup current conversation.
 */
export function setPendingConversation(
  messagesJson: string | null,
  topic: string | null,
): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    if (messagesJson != null) {
      window.sessionStorage.setItem(KEY_MESSAGES_PENDING, messagesJson);
    }
    if (topic != null) {
      window.sessionStorage.setItem(KEY_TOPIC_PENDING, topic);
    }
  } catch {
    // sessionStorage unavailable
  }
}

export interface ConvMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
}

export interface PendingConversation {
  topic: string;
  messages: ConvMessage[];
}

/**
 * Read pending conversation from sessionStorage (set when user clicks "Go to Draw" from Conversation).
 */
export function getPendingConversationFromStorage(): PendingConversation | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const rawMessages = window.sessionStorage.getItem(KEY_MESSAGES_PENDING);
    const topic = window.sessionStorage.getItem(KEY_TOPIC_PENDING) ?? "";
    if (!rawMessages) {
      return null;
    }
    const messages = JSON.parse(rawMessages) as ConvMessage[];
    if (!Array.isArray(messages) || messages.length === 0) {
      return null;
    }
    return { topic, messages };
  } catch {
    return null;
  }
}

/**
 * Convert ConvMessage[] (Entry conversation) to ChatMessage[] for DB persistence.
 * Adds timestamp (ISO string) to each message.
 */
export function pendingConvMessagesToChatMessages(
  messages: ConvMessage[],
): ChatMessage[] {
  const now = new Date();
  return messages.map((msg, i) => ({
    id: msg.id,
    role: msg.role,
    content: msg.content,
    // Use slightly offset timestamps so order is preserved
    timestamp: new Date(now.getTime() + i).toISOString(),
  }));
}

/**
 * Get pending conversation as ChatMessage[] for createNewDiagram, or null if none.
 */
export function getPendingChatHistoryForDiagram(): ChatMessage[] | null {
  const pending = getPendingConversationFromStorage();
  if (!pending || pending.messages.length === 0) {
    return null;
  }
  return pendingConvMessagesToChatMessages(pending.messages);
}
